import { Injectable } from '@angular/core';
import { ImportIntegrationConnection } from '@app/models/import/import-integration-connection.model';
import { ImportIntegrationConsumer } from '@app/models/import/import-integration-consumer.model';
import { ImportIntegration } from '@app/models/import/import-integration.model';
import ConnectionStatus from '@app/types/connection-status.type';
import { environment } from '@env/environment';
import { BehaviorSubject, Observable } from 'rxjs';

const REDIRECT_URL = '/company/import/integrations';

export enum IntegrationStep {
    'LIST',
    'CONNECT',
    'PREVIEW',
    'SUMMARY',
}

@Injectable({ providedIn: 'root' })
export class ImportIntegrationService {
    private _integrationInProgress: BehaviorSubject<ImportIntegration | null> =
        new BehaviorSubject<ImportIntegration | null>(null);
    private _currentStep: BehaviorSubject<IntegrationStep> = new BehaviorSubject<IntegrationStep>(IntegrationStep.LIST);

    // If a user has selected an integration partner, this observable will emit with the integration model. Otherwise it emits null (ie. still on LIST step)
    get integrationInProgress(): Observable<ImportIntegration | null> {
        return this._integrationInProgress.asObservable();
    }

    get currentStep(): IntegrationStep {
        return this._currentStep.getValue();
    }

    // Provides an observable that updates when a user navigates between the various integration steps
    get onStepChange(): Observable<IntegrationStep> {
        return this._currentStep.asObservable();
    }

    goToStep(step: IntegrationStep): void {
        // Reset integration in progress if navigating back to LIST step, since no integration should be in context
        if (step === IntegrationStep.LIST) {
            this._integrationInProgress.next(null);
        }
        this._currentStep.next(step);
    }

    async getIntegrations(): Promise<ImportIntegration[]> {
        const [importIntegrations, consumer] = await Promise.all([
            ImportIntegration.all(),
            ImportIntegrationConsumer.first(),
        ]);

        const consumerIntegrations = consumer.integrations.reduce(
            (acc, { integrationId, status }) => {
                return { ...acc, [integrationId]: status };
            },
            {} as { [key: number]: ConnectionStatus }
        );
        const integrations = importIntegrations[0];

        if (consumer.integrations.length) {
            integrations.forEach((integration) => {
                // If a matching consumer integration is found, update the status field
                if (consumerIntegrations[integration.id]) {
                    integration.status = consumerIntegrations[integration.id];
                }

                return integration;
            });
        }
        return integrations;
    }

    async getIntegrationConnection(integration: ImportIntegration): Promise<ImportIntegrationConnection | undefined> {
        return await ImportIntegrationConnection.param('integrationId', integration.id)
            .queryParams('redirect_url', this.getRedirectUrl(integration.id))
            .show();
    }

    // After an integration has been chosen for connection or import
    beginConnection(integration: ImportIntegration): void {
        this._currentStep.next(IntegrationStep.CONNECT);
        this._integrationInProgress.next(integration);
    }

    async previewImport(integration: ImportIntegration | number): Promise<void> {
        this._currentStep.next(IntegrationStep.PREVIEW);
        // If returning from a unified connection we parse the integration id from a query param so we need to retrieve the list of integrations again
        if (typeof integration === 'number') {
            const foundIntegration = (await this.getIntegrations()).find(
                (i) => i.id === integration && i.status === 'connected'
            );
            if (foundIntegration) {
                this._integrationInProgress.next(foundIntegration);
            } else {
                // If the ID doesn't exist in the integrations list then just return the user to the first step
                this.goToStep(IntegrationStep.LIST);
            }
            // If going straight to the import screen from the list then we already have the integration in context and don't need to retrieve it
        } else {
            this._integrationInProgress.next(integration);
        }
    }

    async doImport(): Promise<void> {
        this.goToStep(IntegrationStep.SUMMARY);
    }

    getRedirectUrl(integrationId: number): string {
        return environment.url + REDIRECT_URL + '?id=' + integrationId;
    }
}
